import { createRef, useMemo, useRef } from 'react';

import {
  ScrollSpyElement,
  ScrollSpyState,
  useScrollSpyState,
} from './useScrollSpyState';

export interface Section {
  id: string;
  title: string;
}

export interface UseScrollSpyResult {
  scrollSpyState: ScrollSpyState;
  sectionRefs: Record<string, React.RefObject<HTMLDivElement>>;
  scrollSpyElements?: ScrollSpyElement[];
}

/**
 * A hook that creates dynamic refs for sections and sets up scroll spy functionality
 *
 * @param sections - Array of section objects with id and title
 * @param scrollContainerRef - Ref for the scrollable container
 * @param isElementVisible - Boolean indicating if the element is visible
 * @returns UseScrollSpyResult object with refs, scroll spy elements and state
 */
export const useScrollSpy = ({
  scrollContainerRef,
  isElementVisible,
  sections,
  scrollOffset,
}: {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  isElementVisible: boolean;
  sections?: Section[];
  scrollOffset?: number;
}): UseScrollSpyResult => {
  // Create refs for each possible section ID
  // This approach creates refs upfront, not in a loop or conditionally
  const refsMap = useRef<Record<string, React.RefObject<HTMLDivElement>>>({});

  // Ensure we have a ref for each section
  const sectionRefs = useMemo(() => {
    // Create any missing refs
    sections?.forEach((section) => {
      if (!refsMap.current[section.id]) {
        refsMap.current[section.id] = createRef<HTMLDivElement>();
      }
    });

    return refsMap.current;
  }, [sections]);

  // Convert sections to the ScrollSpyElement format expected by useScrollSpyState
  const scrollSpyElements = useMemo(() => {
    return sections?.map((section) => ({
      id: section.id,
      title: section.title,
      elementRef: sectionRefs[section.id],
    }));
  }, [sections, sectionRefs]);

  // Use the existing useScrollSpyState with our dynamically created elements
  const scrollSpyState = useScrollSpyState(
    scrollContainerRef,
    isElementVisible,
    scrollSpyElements,
    scrollOffset
  );

  return {
    scrollSpyState,
    sectionRefs,
    scrollSpyElements,
  };
};
